import React from "react";
import styled from "styled-components";

import type { FileConstraints } from "../../../../shared/core/data-forms/file-constraints";
import { acceptedTypeLabels, FileViolation, formatMaxSize } from "../../../../shared/core/data-forms/file-constraints";
import { useIntl } from "../../../../shared/core/i18n/use-intl";
import { theme } from "../../styles/theme";

/** The constraints the form itself carries, told before the file is sent rather than after. */
export const FileConstraintsPanel: React.FC<{
  constraints: FileConstraints;
  violations: FileViolation[];
}> = ({ constraints, violations }) => {
  const { formatMessage } = useIntl();
  const formats = acceptedTypeLabels(constraints.acceptedTypes);

  return (
    <Panel>
      {formats.length > 0 && (
        <Line>
          <Term>{formatMessage("common.fileConstraints.formats")}</Term>
          <Value $failed={violations.includes(FileViolation.WrongType)}>{formats.join(", ")}</Value>
        </Line>
      )}
      {constraints.maxSize !== undefined && (
        <Line>
          <Term>{formatMessage("common.fileConstraints.maxSize")}</Term>
          <Value $failed={violations.includes(FileViolation.TooBig)}>{formatMaxSize(constraints.maxSize)}</Value>
        </Line>
      )}
      {violations.includes(FileViolation.TooBig) && <Fault>{formatMessage("common.fileConstraints.tooBig")}</Fault>}
      {violations.includes(FileViolation.WrongType) && (
        <Fault>{formatMessage("common.fileConstraints.wrongType")}</Fault>
      )}
    </Panel>
  );
};

const Panel = styled.div`
  margin-bottom: 8px;
  padding: 10px 12px;
  border-radius: 8px;
  background-color: #f6f7f8;
`;

const Line = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
`;

const Term = styled.span`
  ${theme.text}
  font-size: 0.8125rem;
  line-height: 1.125rem;
  color: #8c8c8c;
`;

const Value = styled.span<{ $failed: boolean }>`
  ${theme.mediumText}
  flex-shrink: 1;
  margin-left: 12px;
  text-align: right;
  font-size: 0.8125rem;
  line-height: 1.125rem;
  color: ${(props) => (props.$failed ? theme.colors.error[500] : "#0b293e")};
`;

const Fault = styled.div`
  ${theme.text}
  margin-top: 6px;
  font-size: 0.8125rem;
  line-height: 1.125rem;
  color: ${theme.colors.error[500]};
`;
